// Marca del sistema (white-label / Corredata). Mismo patrón que `moneda.js`:
// un store en memoria + una función que aplica los colores a las variables CSS
// de :root en runtime. La fuente de verdad es `cont_config`; si un cliente no ha
// configurado su marca, cae a los valores NEUTROS de Corredata (nunca a los de un
// cliente concreto). Riesgo Unido = Cliente 1: guarda SU marca (nombre/colores y
// el logo de barras vía el centinela `logo_url:'bars'`) en su propio `cont_config`.

// Tipografías disponibles (Google Fonts, precargadas en public/index.html).
// El cliente elige la suya en Admin → Marca. La 1ª es el default.
export const FUENTES = ['Outfit', 'Inter', 'Poppins', 'Montserrat', 'Manrope', 'Nunito', 'Work Sans', 'Lato'];

export const MARCA_DEFAULT = {
  nombre: 'Corredata',
  nombre_corto: 'data',    // sufijo resaltado en el logotipo de texto ("Corre|data")
  tagline: 'Gestión integral para corredores de seguros.',
  abreviatura: 'Corretaje', // cómo se nombra "la casa" en etiquetas (Comisión X, Bolsa X…)
  fuente: 'Outfit',         // tipografía del sistema (de FUENTES)
  logo_url: null,          // null = usa el logo vectorial neutro por defecto (LogoNeutro)
  logo_escala: 1,          // factor de tamaño del logo subido (1 = normal). Tope en UI.
  // Paleta NEUTRA por defecto: TEMA CLARO (fondo blanco, acento gris pizarra). Un
  // cliente entra a un lienzo limpio y lo hace suyo con sus colores. El modo (claro/
  // oscuro) se decide por el brillo del fondo (navy): claro→modo claro, oscuro→oscuro.
  // Riesgo Unido tiene fondo oscuro → se queda en modo oscuro automáticamente.
  colores: {
    navy: '#F5F6F8', steel: '#E4E7EC', sky: '#232B38',
    sky_light: '#3A4557', silver: '#7A8496',   // 7A8496: el gris tenue se lee sobre el fondo claro (contraste ≥ 3, ver revisarLegibilidad)
  },
};

let _marca = { ...MARCA_DEFAULT, colores: { ...MARCA_DEFAULT.colores } };
const LS_KEY = 'ru_marca';   // caché local para brandear el login antes de la sesión

export function getMarca() { return _marca; }
// Abreviatura del corretaje para etiquetas ("Comisión {abrev}", "Bolsa {abrev}"…).
export function getAbrev() { return _marca?.abreviatura || MARCA_DEFAULT.abreviatura; }

// Aplica la tipografía elegida a la variable CSS --font-sans (todo el sistema la
// usa). Las fuentes están precargadas en index.html, así que basta cambiar la var.
export function aplicarFuente(fuente) {
  if (typeof document === 'undefined') return;
  const f = FUENTES.includes(fuente) ? fuente : MARCA_DEFAULT.fuente;
  document.documentElement.style.setProperty('--font', `'${f}', sans-serif`);
}

// Aplica el logo del cliente como favicon de la pestaña. Logo subido (data URI /
// URL) → tal cual; centinela 'bars' (RU) → se genera un SVG de barras con su
// paleta; sin logo → se deja el favicon neutro estático de index.html.
function aplicarFavicon(m) {
  if (typeof document === 'undefined' || !m?.logo_url) return;
  let href;
  if (m.logo_url === 'bars') {
    const c = m.colores || MARCA_DEFAULT.colores;
    const skyDark = mezclar(c.steel, c.sky, 0.5);
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 35 40'>`
      + `<rect x='0' y='22' width='7.2' height='18' rx='2.2' fill='${c.steel}' opacity='0.7'/>`
      + `<rect x='9.2' y='14' width='7.2' height='26' rx='2.2' fill='${skyDark}' opacity='0.85'/>`
      + `<rect x='18.4' y='6' width='7.2' height='34' rx='2.2' fill='${c.sky}'/>`
      + `<rect x='27.6' y='0' width='7.2' height='40' rx='2.2' fill='${c.sky_light}'/></svg>`;
    href = 'data:image/svg+xml,' + encodeURIComponent(svg);
  } else if (/^(data:|https?:)/.test(m.logo_url)) {
    href = m.logo_url;
  } else return;
  let link = document.querySelector("link[rel='icon']");
  if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
  link.href = href;
}

// Al arrancar (antes del login) aplica la última marca conocida desde localStorage,
// para que la pantalla de login muestre la marca del cliente y no la de fábrica.
export function cargarMarcaLocal() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const m = JSON.parse(raw);
      _marca = { ...MARCA_DEFAULT, ...m, colores: { ...MARCA_DEFAULT.colores, ...(m.colores || {}) } };
    }
  } catch { /* caché corrupta: se queda con el default */ }
  // Aplica SIEMPRE (con caché o con el default neutro) para que el login de un
  // navegador nuevo muestre el look claro, no el tema oscuro base del CSS.
  aplicarColoresCSS(_marca.colores);
  aplicarFuente(_marca.fuente);
  aplicarFavicon(_marca);
  return _marca;
}

// --- utilidades de color (para derivar sky-dark y navy-grad de la paleta) ---
function hexAeRgb(hex) {
  const h = String(hex || '').replace('#', '');
  const n = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  return [0, 2, 4].map((i) => parseInt(n.slice(i, i + 2), 16) || 0);
}
function rgbAeHex([r, g, b]) {
  return '#' + [r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
}
// Mezcla dos hex: t=0 → a, t=1 → b.
function mezclar(a, b, t) {
  const A = hexAeRgb(a), B = hexAeRgb(b);
  return rgbAeHex(A.map((v, i) => v + (B[i] - v) * t));
}

// ── LEGIBILIDAD de una paleta (pedido por Ricardo 2026-10-03) ─────────────────────────
// Con ciertas mezclas el texto se pierde (pasó: respuestas del Copiloto blancas sobre
// blanco). Antes de guardar la marca se revisan las combinaciones que de verdad usa el
// sistema, con el MISMO criterio de tema que aplicarColoresCSS (fondo claro → texto
// oscuro y tarjetas blancas; fondo oscuro → texto blanco y tarjetas = superficie al 40%).
// Contraste según WCAG: 4.5 para el texto normal, 3 para lo grande o resaltado.
function luminancia(hex) {
  const [r, g, b] = hexAeRgb(hex).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
export function contraste(a, b) {
  const [l1, l2] = [luminancia(a), luminancia(b)].sort((x, y) => y - x);
  return (l1 + 0.05) / (l2 + 0.05);
}
// Devuelve [] si todo se lee bien, o la lista de problemas en palabras del usuario.
export function revisarLegibilidad(colores) {
  const c = { ...MARCA_DEFAULT.colores, ...(colores || {}) };
  const [r, g, b] = hexAeRgb(c.navy);
  const claro = (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6;
  const texto = claro ? '#1E2A3A' : '#FFFFFF';
  const tarjeta = claro ? '#FFFFFF' : mezclar(c.navy, c.steel, 0.4);
  const reglas = [
    { que: 'El texto principal sobre el color de Fondo', a: texto, b: c.navy, min: 4.5, cambia: 'Fondo (Navy)' },
    { que: 'El texto principal dentro de tarjetas y ventanas', a: texto, b: tarjeta, min: 4.5, cambia: claro ? 'Fondo (Navy)' : 'Superficies (Steel)' },
    { que: 'Las etiquetas y textos tenues sobre el Fondo', a: c.silver, b: c.navy, min: 3, cambia: 'Texto tenue (Silver)' },
    { que: 'Las etiquetas y textos tenues dentro de tarjetas', a: c.silver, b: tarjeta, min: 3, cambia: 'Texto tenue (Silver)' },
    { que: 'El texto blanco de los botones principales', a: '#FFFFFF', b: c.sky, min: 3, cambia: 'Primario (Sky)' },
    { que: 'Los resaltados (montos, números de recibo, enlaces)', a: c.sky_light, b: tarjeta, min: 3, cambia: 'Primario claro (Sky light)' },
    { que: 'El texto de los encabezados de ventanas', a: texto, b: c.steel, min: 3, cambia: 'Superficies (Steel)' },
  ];
  return reglas
    .map((x) => ({ ...x, ratio: contraste(x.a, x.b) }))
    .filter((x) => x.ratio < x.min)
    .map(({ que, ratio, min, cambia }) => ({ que, ratio: Math.round(ratio * 10) / 10, min, cambia }));
}

// Aplica una paleta a las variables CSS de :root. Reutilizable para la vista
// previa en vivo del panel de Admin (se le pasa una paleta temporal).
export function aplicarColoresCSS(colores) {
  const c = { ...MARCA_DEFAULT.colores, ...(colores || {}) };
  const root = document.documentElement;
  const set = (name, val) => root.style.setProperty(name, val);
  const canal = (hex) => hexAeRgb(hex).join(',');
  // Colores sólidos.
  set('--navy', c.navy); set('--steel', c.steel); set('--sky', c.sky);
  set('--sky-light', c.sky_light); set('--silver', c.silver);
  // Canales RGB (para tints rgba(var(--x-rgb), A)).
  set('--navy-rgb', canal(c.navy)); set('--steel-rgb', canal(c.steel));
  set('--sky-rgb', canal(c.sky)); set('--sky-light-rgb', canal(c.sky_light));
  set('--silver-rgb', canal(c.silver));
  // navy con transparencia (--navy-80) y derivados de marca (logo/fondo).
  const [nr, ng, nb] = hexAeRgb(c.navy);
  set('--navy-80', `rgba(${nr},${ng},${nb},0.8)`);
  set('--sky-dark', mezclar(c.steel, c.sky, 0.5));
  set('--navy-grad', mezclar(c.navy, c.steel, 0.25));
  // Modo claro/oscuro según el brillo del fondo (navy). Fondo claro → tema claro
  // (texto oscuro, tarjetas blancas); fondo oscuro → tema oscuro (el de siempre).
  // Riesgo Unido (navy oscuro) cae en oscuro sin tocar nada.
  const [r, g, b] = hexAeRgb(c.navy);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  if (lum > 0.6) root.setAttribute('data-theme', 'claro');
  else root.removeAttribute('data-theme');
}

// Piezas de marca para los documentos IMPRESOS (recibos/PDF). Como el HTML de
// impresión vive en un iframe/ventana aparte SIN las variables CSS, aquí se
// entregan los valores concretos (hex + nombre + tagline + logo embebido).
export function marcaImpresion(altoLogo = 40) {
  const m = getMarca();
  const c = m.colores;
  const skyDark = mezclar(c.steel, c.sky, 0.5);
  // Barras (logo de Riesgo Unido, centinela 'bars').
  const barrasSVG = `<svg width="${altoLogo * 0.875}" height="${altoLogo}" viewBox="0 0 35 40" xmlns="http://www.w3.org/2000/svg">`
    + `<rect x="0" y="22" width="7.2" height="18" rx="2.2" fill="${c.steel}" opacity="0.7"/>`
    + `<rect x="9.2" y="14" width="7.2" height="26" rx="2.2" fill="${skyDark}" opacity="0.85"/>`
    + `<rect x="18.4" y="6" width="7.2" height="34" rx="2.2" fill="${c.sky}"/>`
    + `<rect x="27.6" y="0" width="7.2" height="40" rx="2.2" fill="${c.sky_light}"/></svg>`;
  // Marca neutra de Corredata (anillo "C" + nodo), coloreada por la paleta.
  const neutroSVG = `<svg width="${altoLogo}" height="${altoLogo}" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">`
    + `<path d="M35.5 14.4 A15 15 0 1 0 35.5 33.6" stroke="${c.sky}" stroke-width="5.5" stroke-linecap="round" fill="none"/>`
    + `<circle cx="36" cy="24" r="5.5" fill="${c.sky_light}"/></svg>`;
  const logo = m.logo_url === 'bars'
    ? barrasSVG
    : m.logo_url
      ? `<img src="${m.logo_url}" alt="" style="height:${altoLogo}px;width:auto;max-width:${altoLogo * 4}px;object-fit:contain"/>`
      : neutroSVG;
  return { nombre: m.nombre, tagline: m.tagline, abreviatura: getAbrev(), fuente: m.fuente || MARCA_DEFAULT.fuente, colores: c, skyDark, logo };
}

// Procesa un archivo de logo → data URI listo para guardar en cont_config.
// SVG se guarda tal cual (vectorial, liviano). Raster se reescala a `maxDim` px
// y se re-comprime para que pese poco. Lanza Error con mensaje si algo no cuadra.
export const LOGO_TIPOS = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
const LOGO_MAX_ORIGINAL = 3 * 1024 * 1024;   // 3 MB de entrada máximo
const LOGO_MAX_RESULTADO = 300 * 1024;        // ~300 KB del data URI final

export function procesarLogo(file, maxDim = 400) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error('No se seleccionó ningún archivo.'));
    if (!LOGO_TIPOS.includes(file.type)) {
      return reject(new Error('Formato no soportado. Usa PNG, JPG, WEBP o SVG.'));
    }
    if (file.size > LOGO_MAX_ORIGINAL) {
      return reject(new Error('La imagen pesa más de 3 MB. Usa una más liviana.'));
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));

    // SVG: se guarda tal cual como data URI.
    if (file.type === 'image/svg+xml') {
      reader.onload = () => resolve(String(reader.result));
      reader.readAsDataURL(file);
      return;
    }

    // Raster: reescalar en canvas y re-comprimir.
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('No se pudo procesar la imagen.'));
      img.onload = () => {
        let { width: w, height: h } = img;
        const escala = Math.min(1, maxDim / Math.max(w, h));
        w = Math.round(w * escala); h = Math.round(h * escala);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        // PNG conserva transparencia (los logos suelen necesitarla).
        const uri = canvas.toDataURL('image/png');
        if (uri.length > LOGO_MAX_RESULTADO) {
          return reject(new Error('El logo quedó muy pesado tras optimizar. Prueba con una imagen más simple o un SVG.'));
        }
        resolve(uri);
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
}

// Toma una fila de cont_config (con o sin columnas de marca) → normaliza,
// guarda en el store y aplica los colores. Devuelve la marca resultante.
export function aplicarMarca(cfg) {
  const d = MARCA_DEFAULT;
  const colores = {
    navy: cfg?.color_navy || d.colores.navy,
    steel: cfg?.color_steel || d.colores.steel,
    sky: cfg?.color_sky || d.colores.sky,
    sky_light: cfg?.color_sky_light || d.colores.sky_light,
    silver: cfg?.color_silver || d.colores.silver,
  };
  _marca = {
    nombre: cfg?.marca_nombre || d.nombre,
    nombre_corto: cfg?.marca_nombre_corto ?? d.nombre_corto,
    tagline: cfg?.marca_tagline || d.tagline,
    abreviatura: (cfg?.marca_abreviatura && String(cfg.marca_abreviatura).trim()) || d.abreviatura,
    fuente: cfg?.marca_fuente || d.fuente,
    logo_url: cfg?.marca_logo_url || null,
    logo_escala: Number(cfg?.marca_logo_escala) || d.logo_escala,
    colores,
  };
  aplicarColoresCSS(colores);
  aplicarFuente(_marca.fuente);
  aplicarFavicon(_marca);
  try { localStorage.setItem(LS_KEY, JSON.stringify(_marca)); } catch { /* cuota llena: no pasa nada */ }
  return _marca;
}
